import { Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';

import { AffiliateWorkflowBuilderHelper } from './affiliate-workflow-builder-helper';

interface WorkflowBuilderTabProps {
  tabValue: string;
  label: string;
  isActive: boolean;
  onSelect: (tabValue: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  /** Absent means the tab cannot be closed, which is how the workflow tab renders. */
  onClose?: () => void;
}

/** One document tab. Equal width by design: the two tabs split the strip rather than sizing to
 * their labels. The close control is a sibling of the tab button, not a child, because a button
 * inside a button is invalid. */
export const WorkflowBuilderTab = ({
  tabValue,
  label,
  isActive,
  onSelect,
  onKeyDown,
  onClose,
}: WorkflowBuilderTabProps) => (
  <div
    className={cn(
      'flex h-7 min-w-0 flex-1 items-center gap-xs rounded-sm border-1/2 pl-md transition-colors',
      onClose ? 'pr-sm' : 'pr-md',
      isActive
        ? 'border-border-primary bg-bg-primary'
        : 'border-transparent hover:bg-bg-secondary-hover'
    )}
  >
    <button
      type="button"
      role="tab"
      id={AffiliateWorkflowBuilderHelper.getTabId(tabValue)}
      aria-selected={isActive}
      aria-controls={AffiliateWorkflowBuilderHelper.getTabPanelId(tabValue)}
      tabIndex={isActive ? 0 : -1}
      onClick={() => onSelect(tabValue)}
      onKeyDown={onKeyDown}
      className={cn(
        'txt-extra-small-medium min-w-0 flex-1 truncate text-left',
        isActive ? 'text-text-primary' : 'text-text-tertiary'
      )}
    >
      {label}
    </button>
    {onClose && (
      <button
        type="button"
        aria-label={`Close ${label}`}
        onClick={onClose}
        className="flex size-5 shrink-0 items-center justify-center rounded-xs text-text-tertiary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
      >
        <Icons.XClose aria-hidden="true" className="size-3" />
      </button>
    )}
  </div>
);
